import { useState, useEffect, useCallback, useRef } from 'react';
import { lunchOrdersApiService, SheetData } from '../services/lunchOrdersApiService';

function isWithinAutoRefreshWindow(): boolean {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu
  const hour = now.getHours();

  const isTuesdayOrThursday = day === 2 || day === 4;
  const isBetween9And12 = hour >= 9 && hour < 12;

  return isTuesdayOrThursday && isBetween9And12;
}

const CHECK_INTERVAL_MS = 60 * 1000;       // Check window every 60s
const REFRESH_INTERVAL_MS = 15 * 60 * 1000; // Refetch every 15min

export function useLunchOrdersData() {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [isAutoRefreshActive, setIsAutoRefreshActive] = useState(false);

  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await lunchOrdersApiService.fetchLunchOrders();
      setSheets(data.sheets);
      setFetchedAt(data.fetchedAt);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch lunch orders');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh logic: check every 60s if we're in the window
  useEffect(() => {
    const checkInterval = setInterval(() => {
      const inWindow = isWithinAutoRefreshWindow();
      setIsAutoRefreshActive(inWindow);

      if (inWindow && !refreshIntervalRef.current) {
        // Start 15-min refresh interval
        refreshIntervalRef.current = setInterval(() => {
          fetchData();
        }, REFRESH_INTERVAL_MS);
      } else if (!inWindow && refreshIntervalRef.current) {
        // Stop refresh interval when outside window
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }, CHECK_INTERVAL_MS);

    // Also check immediately on mount
    const inWindow = isWithinAutoRefreshWindow();
    setIsAutoRefreshActive(inWindow);
    if (inWindow) {
      refreshIntervalRef.current = setInterval(() => {
        fetchData();
      }, REFRESH_INTERVAL_MS);
    }

    return () => {
      clearInterval(checkInterval);
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [fetchData]);

  return { sheets, loading, error, fetchedAt, refetch: fetchData, isAutoRefreshActive };
}
